import { getRouteApi } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

import { useAdminAgentNoteStats } from '@apps/web/features/admin_agent_notes/api/agent_notes_queries';
import { ANALYTICS_TIMEZONE } from '@apps/web/features/admin_agent_notes/model/constants';
import {
    type AgentNoteRangePreset,
    type RangeSelection,
    resolveRangePreset,
} from '@apps/web/features/admin_agent_notes/model/range_presets';
import { type AgentNotesSearch } from '@apps/web/features/admin_agent_notes/model/search_params';
import { getDateStringInTimezone } from '@apps/web/lib/date_utils';
import { type components } from '@libraries/typescript/api_client';

export { ANALYTICS_TIMEZONE } from '@apps/web/features/admin_agent_notes/model/constants';

const route = getRouteApi('/_app/admin/agent-notes/');

const DEFAULT_RANGE_PRESET: AgentNoteRangePreset = '14d';

/**
 * Controller for the Dashboard tab: one global date range (relative preset
 * or fixed dates, URL-owned) plus the org filter, driving a single
 * window-scoped stats query.
 */
export function useAgentNotesDashboard() {
    const search = route.useSearch();
    const navigate = route.useNavigate();

    const today = getDateStringInTimezone(new Date(), ANALYTICS_TIMEZONE);
    const hasFixedRange =
        search.created_after !== undefined ||
        search.created_before !== undefined;
    const preset = hasFixedRange
        ? undefined
        : (search.range ?? DEFAULT_RANGE_PRESET);
    const resolved = preset
        ? resolveRangePreset(preset, today)
        : {
              from: search.created_after ?? today,
              to: search.created_before ?? today,
          };
    const range = useMemo(
        () => ({ from: resolved.from, to: resolved.to }),
        [resolved.from, resolved.to],
    );

    const statsQuery = useAdminAgentNoteStats(
        {
            organizationId: search.organization_id,
            startDate: range.from,
            endDate: range.to,
        },
        { keepPreviousData: true },
    );

    // The notes-activity calendar always covers Jan 1 -> today, scoped by
    // org but NOT by the dashboard date filter. The grid always pads out
    // the rest of the year for a full GitHub-style view.
    const heatmapStatsQuery = useAdminAgentNoteStats(
        {
            organizationId: search.organization_id,
            startDate: `${today.slice(0, 4)}-01-01`,
            endDate: today,
        },
        { keepPreviousData: true },
    );

    const setRangeSelection = useCallback(
        (selection: RangeSelection) => {
            void navigate({
                search: (prev) => ({
                    ...prev,
                    range:
                        selection.kind === 'preset'
                            ? selection.preset
                            : undefined,
                    created_after:
                        selection.kind === 'fixed' ? selection.from : undefined,
                    created_before:
                        selection.kind === 'fixed' ? selection.to : undefined,
                }),
                replace: true,
                resetScroll: false,
            });
        },
        [navigate],
    );

    const setOrganization = useCallback(
        (organizationId: string | undefined) => {
            void navigate({
                search: (prev) => ({
                    ...prev,
                    organization_id: organizationId,
                }),
                replace: true,
                resetScroll: false,
            });
        },
        [navigate],
    );

    // Notes-tab search for KPI drill-downs, preserving the org filter and
    // the same window (relative presets stay relative).
    const buildNotesSearch = useCallback(
        (
            status?: components['schemas']['AgentNoteStatus'],
        ): AgentNotesSearch => ({
            tab: 'notes',
            page: 1,
            status,
            organization_id: search.organization_id,
            ...(preset
                ? { range: preset }
                : {
                      created_after: range.from,
                      created_before: range.to,
                  }),
        }),
        [preset, range.from, range.to, search.organization_id],
    );

    return {
        organizationId: search.organization_id,
        range,
        buildNotesSearch,
        rangePreset: preset,
        fixedRange: hasFixedRange
            ? { from: search.created_after, to: search.created_before }
            : null,
        stats: statsQuery.data,
        isLoading: statsQuery.isLoading,
        hasError:
            statsQuery.error !== undefined ||
            (statsQuery.hasRequestFailed && statsQuery.data === undefined),
        heatmapStats: heatmapStatsQuery.data,
        setRangeSelection,
        setOrganization,
    };
}
